import { CleaveInput, preventSubmit, ValidatedSelect, ValidatedSelectItem } from '@cp-shared-19/frontend-ui';
import {
    Button,
    ButtonContainer,
    Fieldset,
    Form,
    Layout,
    RadioButton,
    FormFieldRadioButtonGroup,
    Paragraph,
} from '@vwfs-bronson/bronson-react';
import { dashboardPagePath } from 'components/navigation/paths';
import { Formik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { partialEarlySettlementValidationSchema } from 'cpfr-common';
import { monthNames } from '../../../../../utils/monthNames';
import { getInitialValues } from './initialValues';
import { EmailInputModal } from '../../../../request/form-view/email-input/EmailInputModal';
import { getEarlySettlementReasons } from 'components/early-settlement/joint-entry-point/utils';

export type FormData = {
    requestDate: string;
    amount: string;
    goal: string;
    reason: string;
    email?: string;
};

type PartialSettlementFormProps = {
    handleSubmit: (data: FormData) => void;
    errorCode?: string;
    customerType: string;
};

const availabelGoals = [
    {
        label: 'Réduire le montant des échéances mensuelles',
        value: 'MONTHLY_AMOUNT_REDUCTION',
    },
    {
        label: 'Réduire la durée totale de votre financement',
        value: 'INSTALLMENT_NUMBER_REDUCTION',
    },
];

const availableMonths: ValidatedSelectItem[] = [];

monthNames.forEach((month, index) =>
    availableMonths.push({
        label: month,
        value: moment()
            .set({ month: index, date: moment().month(index).daysInMonth() })
            .format('YYYY-MM-DD'),
    }),
);

const translationPrefix = 'partial-early-settlement.form';

export const PartialEarlySettlementForm: React.FC<PartialSettlementFormProps> = ({
    handleSubmit,
    errorCode,
    customerType,
}) => {
    const { t } = useTranslation('early-settlement');
    const history = useHistory();
    const [error, setError] = useState<string | null>(errorCode || null);
    useEffect(() => {
        setError(errorCode || null);
    }, [errorCode]);
    const translationFormErrorsPrefix = 'partial-early-settlement.form.errors';
    const mandatoryFieldsInfo = t('joint-entry-point.mandatory-fields-info');
    const availableReasons: ValidatedSelectItem[] = getEarlySettlementReasons(customerType);
    const errorMessages = {
        requestDate: {
            required: t(`${translationFormErrorsPrefix}.empty-date`),
        },
        reason: {
            required: t(`${translationFormErrorsPrefix}.empty-reason`),
        },
        amount: {
            required: t(`${translationFormErrorsPrefix}.empty-amount`),
        },
        goal: {
            required: '',
        },
        email: {
            required: t(`${translationFormErrorsPrefix}.required`),
            mustBeEmail: t(`${translationFormErrorsPrefix}.mustBeEmail`),
        },
    };

    return (
        <Formik
            initialValues={getInitialValues()}
            onSubmit={(data: FormData): void => {
                setError(null);
                handleSubmit(data);
            }}
            validationSchema={partialEarlySettlementValidationSchema(
                errorMessages,
                error === 'EMAIL_NOT_FOUND_OR_PROVIDED',
            )}
        >
            {(formik): JSX.Element => (
                <Form onSubmit={(e) => preventSubmit(e)} data-testid="partial-settlement-form">
                    <Fieldset>
                        <Fieldset.Row>
                            <Layout>
                                <Layout.Item default={'1/2'} m={'1/1'}>
                                    <ValidatedSelect
                                        className="c-form-field--no-floating"
                                        required={true}
                                        aria-required={true}
                                        requiredText=" *"
                                        isMandatory={true}
                                        name={'requestDate'}
                                        label={t(`${translationPrefix}.request-date`)}
                                        placeholder={t(`${translationPrefix}.request-date-placeholder`)}
                                        selectItems={availableMonths.map(({ value, label }) => ({
                                            value,
                                            label,
                                        }))}
                                    />
                                </Layout.Item>
                                <Layout.Item default={'1/2'} m={'1/1'}>
                                    <CleaveInput
                                        requiredText=" *"
                                        required={true}
                                        aria-required={true}
                                        isMandatory={true}
                                        cleaveOptions={{
                                            numeral: true,
                                            delimiter: '',
                                            numeralDecimalMark: ',',
                                        }}
                                        className="c-form-field--no-floating"
                                        name="amount"
                                        addonText="€"
                                        label={t(`${translationPrefix}.amount`)}
                                    />
                                </Layout.Item>
                            </Layout>
                        </Fieldset.Row>
                        <Fieldset.Row>
                            <p>
                                {t(`${translationPrefix}.goal`)} <span aria-hidden="true"> *</span>
                            </p>
                            <FormFieldRadioButtonGroup>
                                {availabelGoals.map(({ value, label }, index) => (
                                    <RadioButton
                                        key={index}
                                        testId={`goal-${index}`}
                                        name="goal"
                                        aria-required={true}
                                        required={true}
                                        value={value}
                                        defaultChecked={index === 0}
                                        onChange={(): void => {
                                            formik.setFieldValue('goal', value);
                                        }}
                                    >
                                        {label}
                                    </RadioButton>
                                ))}
                            </FormFieldRadioButtonGroup>
                        </Fieldset.Row>
                        <Fieldset.Row>
                            <ValidatedSelect
                                className="c-form-field--no-floating"
                                isMandatory={true}
                                required={true}
                                requiredText=" *"
                                name={'reason'}
                                label={t(`${translationPrefix}.reason`)}
                                placeholder={t(`${translationPrefix}.reason-placeholder`)}
                                selectItems={availableReasons.map(({ value, label }) => ({
                                    value,
                                    label,
                                }))}
                            />
                        </Fieldset.Row>
                        <Fieldset.Row>
                            <Paragraph>{t(`${translationPrefix}.reason-desc`)}</Paragraph>
                        </Fieldset.Row>
                        <Fieldset.Row>
                            <ButtonContainer center>
                                <Button
                                    secondary
                                    onClick={(): void => history.push(dashboardPagePath())}
                                    testId="cancelButton"
                                >
                                    {t('translation:editable-section-nav.cancel')}
                                </Button>
                                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
                                <Button onClick={formik.handleSubmit as any} type="button" testId="send">
                                    {t('translation:editable-section-nav.send')}
                                </Button>
                            </ButtonContainer>
                        </Fieldset.Row>
                    </Fieldset>
                    <p>{mandatoryFieldsInfo}</p>
                    <EmailInputModal
                        show={error === 'EMAIL_NOT_FOUND_OR_PROVIDED'}
                        onClose={() => setError(null)}
                        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
                        onConfirm={formik.handleSubmit as any}
                    />
                </Form>
            )}
        </Formik>
    );
};
